// Ce fichier gère l'authentification et met à jour l'état d'authentification dans toute l'application.

import { createApi, fetchBaseQuery } from "@reduxjs/toolkit/query/react";
import { createSlice } from "@reduxjs/toolkit";

// Définit l'état initial pour l'authentification
interface AuthState {
  isAuthenticated: boolean;
  token: string | null;
  status: "idle" | "loading" | "succeeded" | "failed";
  error: string | null;
}

const initialState: AuthState = {
  isAuthenticated: localStorage.getItem("token") ? true : false, // Vérifie si le token existe dans localStorage
  token: localStorage.getItem("token") ?? null, // Initialise le token à partir de localStorage ou null
  status: "idle", // Le statut initial est inactif
  error: null, // Aucune erreur initiale
};

// Définit la structure de la réponse API pour la connexion
interface LoginResponse {
  token: string;
}

// Définit la structure des informations d'identification de connexion
interface LoginCredentials {
  username: string;
  password: string;
}

// URL de base de l'API
const API_URL = `${import.meta.env.VITE_API_URL}/api/v1/s/auth`;

// Crée un slice API avec des endpoints d'authentification en utilisant createApi
export const authApi = createApi({
  reducerPath: "authApi", // Nom sous lequel le slice API sera ajouté au store
  baseQuery: fetchBaseQuery({ baseUrl: API_URL }), // URL de base pour les requêtes API
  endpoints: (builder) => ({
    loginUser: builder.mutation<LoginResponse, LoginCredentials>({
      query: (credentials) => ({
        url: "/", // URL de l'endpoint
        method: "POST", // Méthode HTTP
        body: credentials, // Corps de la requête contenant les informations d'identification de connexion
      }),
    }),
  }),
});

// Extrait la fonction de mutation pour la connexion à partir de authApi
export const { useLoginUserMutation } = authApi;

// Crée un slice pour la gestion de l'état d'authentification en utilisant createSlice
const authSlice = createSlice({
  name: "auth", // Nom du slice
  initialState, // État initial défini ci-dessus
  reducers: {
    // Fonction réductrice pour se déconnecter
    logout(state) {
      state.isAuthenticated = false; // Définit isAuthenticated à false
      state.token = null; // Efface le token
      localStorage.removeItem("token"); // Supprime le token de localStorage
    },
    // Fonction réductrice pour vérifier le statut d'authentification
    checkAuth(state) {
      const token = localStorage.getItem("token"); // Récupère le token depuis localStorage
      if (token) {
        state.isAuthenticated = true; // Définit isAuthenticated à true si le token existe
        state.token = token; // Définit le token
      }
    },
  },
  extraReducers: (builder) => {
    // Gère les réducteurs supplémentaires pour le cycle de vie de la mutation de connexion
    builder.addMatcher(authApi.endpoints.loginUser.matchPending, (state) => {
      state.status = "loading"; // Définit le statut à chargement pendant la demande de connexion
    });
    builder.addMatcher(
      authApi.endpoints.loginUser.matchFulfilled,
      (state, { payload }) => {
        state.status = "succeeded"; // Définit le statut à réussi après une connexion réussie
        state.token = payload.token; // Définit le token à partir de la réponse de connexion
        state.isAuthenticated = true; // Définit isAuthenticated à true
        localStorage.setItem("token", payload.token); // Stocke le token dans localStorage
      }
    );
    builder.addMatcher(
      authApi.endpoints.loginUser.matchRejected,
      (state, { error }) => {
        state.status = "failed"; // Définit le statut à échoué en cas de rejet de la connexion
        state.error = error.message || "Échec de la connexion"; // Définit le message d'erreur
      }
    );
  },
});

// Exporte les actions et le réducteur depuis authSlice
export const { logout, checkAuth } = authSlice.actions;
export default authSlice.reducer; // Exporte la fonction réductrice authSlice
